import { useCallback } from 'react';
import { useEditor } from '../context/EditorContext';
import { saveProject, buildProjectPayload, getProject, apiErrorMessage } from '../lib/projectApi';
import { pagesToPdf, renderPageToCanvas, canvasToBlob, downloadBlob, safeFileName } from '../lib/renderUtils';
import { capturePublicationThumbnail } from '../lib/sheetRender';
import { touchRecent } from '../lib/recents';
import { toast } from 'sonner';

/**
 * Publication-level commands shared by the Backstage sections (Save, Save As, Share, Close, Home):
 * cloud save, project-file download, PDF / image export, open-from-cloud and "close publication".
 */
/** Saves run one at a time: a manual Save that lands while autosave is creating the publication waits for it and then updates it (never a second copy). */
let saveInFlight = null;

export const usePublicationActions = () => {
  const { dispatch, rawDispatch, stateRef, getCurrentPage } = useEditor();

  /**
   * Save to the cloud (update, or create when `asNew` / never saved). Resolves with the saved project or null on failure.
   * `thumbnail: false` skips the page-1 snapshot (autosave while typing keeps the previous one).
   */
  const saveToCloud = useCallback(async ({ name, asNew = false, silent = false, quiet = false, thumbnail: withThumbnail = true } = {}) => {
    if (name !== undefined && !String(name ?? '').trim()) { if (!quiet) toast.error('Give your publication a name first.'); return null; }
    if (saveInFlight) { try { await saveInFlight; } catch { /* the earlier save reports its own failure */ } }
    const run = async () => {
      const s = stateRef.current;
      const finalName = (name ?? s.projectName ?? '').trim() || 'Untitled Publication';
      const revision = s.docRevision; // edits made while the request is in flight stay "unsaved"
      try {
        const thumbnail = withThumbnail || !s.projectId ? await capturePublicationThumbnail(s) : undefined; // page-1 snapshot for the Home / Open lists (optional)
        const saved = await saveProject(s, { name: finalName, asNew, thumbnail });
        if (stateRef.current.projectId !== saved.id) dispatch({ type: 'SET_PROJECT_ID', payload: saved.id });
        if (stateRef.current.projectName !== saved.name) dispatch({ type: 'SET_PROJECT_NAME', payload: saved.name });
        rawDispatch({ type: 'MARK_SAVED', payload: { at: saved.updated_at, revision } });
        touchRecent(saved.id);
        if (!silent && !quiet) toast.success(asNew ? 'Saved as a new publication' : 'Publication saved');
        return saved;
      } catch (e) {
        if (!quiet) toast.error(await apiErrorMessage(e, 'Could not save the publication.'));
        return null;
      }
    };
    const mine = run();
    saveInFlight = mine;
    try { return await mine; } finally { if (saveInFlight === mine) saveInFlight = null; }
  }, [dispatch, rawDispatch, stateRef]);

  /** Download the publication as a WannaPublish project file (.json). */
  const downloadProjectFile = useCallback((name) => {
    const s = stateRef.current;
    const finalName = (name ?? s.projectName ?? '').trim() || 'publication';
    const payload = { ...buildProjectPayload(s, finalName), id: s.projectId };
    downloadBlob(new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' }), `${safeFileName(finalName)}.wannapublish.json`);
    toast.success('Project file downloaded');
  }, [stateRef]);

  /** Export to 'pdf' | 'png' | 'jpg'. `scope` = 'all' | 'current'. */
  const exportAs = useCallback(async (kind, { scope = 'all', scale = 2, onProgress, name } = {}) => {
    const s = stateRef.current;
    const pages = scope === 'all' ? s.pages : [getCurrentPage()];
    const base = safeFileName(name || s.projectName);
    try {
      if (kind === 'pdf') {
        const pdf = await pagesToPdf(pages, s.masters, s.textFlow, { scale, onProgress: (i, n) => onProgress && onProgress(`Rendering page ${i} of ${n}…`) });
        pdf.save(`${base}.pdf`);
      } else {
        for (let i = 0; i < pages.length; i++) {
          onProgress && onProgress(`Rendering page ${i + 1} of ${pages.length}…`);
          const canvas = await renderPageToCanvas(pages[i], s.masters[pages[i].masterId || 'default'], s.textFlow, scale);
          const blob = await canvasToBlob(canvas, kind === 'jpg' ? 'image/jpeg' : 'image/png', 0.92);
          downloadBlob(blob, pages.length > 1 ? `${base}-page${i + 1}.${kind}` : `${base}.${kind}`);
          if (i < pages.length - 1) await new Promise((r) => setTimeout(r, 400));
        }
      }
      toast.success(`${kind.toUpperCase()} export complete`);
      return true;
    } catch (e) {
      console.error(e);
      toast.error('Export failed. Pictures from other websites may block export — try re-inserting them from Online Pictures.');
      return false;
    } finally { onProgress && onProgress(''); }
  }, [stateRef, getCurrentPage]);

  /** Open a cloud publication by id. */
  const openFromCloud = useCallback(async (id) => {
    try {
      const full = await getProject(id);
      dispatch({ type: 'SET_PROJECT', payload: full });
      touchRecent(full.id);
      toast.success(`Opened “${full.name}”`);
      return full;
    } catch (e) {
      toast.error(await apiErrorMessage(e, 'Could not open the publication.'));
      return null;
    }
  }, [dispatch]);

  /**
   * Close a publication (its document tab). The next tab becomes active; closing the last one leaves an empty
   * publication and the File ▸ Home screen — the "nothing open" state.
   */
  const closePublication = useCallback((tabId) => {
    rawDispatch({ type: 'CLOSE_DOC_TAB', payload: { id: tabId || stateRef.current.activeTabId } });
  }, [rawDispatch, stateRef]);

  return { saveToCloud, downloadProjectFile, exportAs, openFromCloud, closePublication };
};
